import { Search, X } from 'lucide-react';
import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { SearchContext } from '../../../context/search/SearchContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../config/firebase';

function SearchBar() {
  const { fetchSuggestions, suggestions, setSuggestions } =
    useContext(SearchContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(e.target) &&
        window.innerWidth >= 1024
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const debouncedFetch = useMemo(
    () => {
      let timer;
      return (text) => {
        clearTimeout(timer);
        timer = setTimeout(() => fetchSuggestions(text), 500);
      };
    },
    [fetchSuggestions]
  );

  const handleChange = (e) => {
    const text = e.target.value;
    setSearchQuery(text);

    const trimmedText = text.trim();

    if (trimmedText.length >= 2) {
      debouncedFetch(trimmedText);
    } else {
      setSuggestions([]);
    }
  };

  const fetchSearch = (s) => {
    saveRecentSearch(s.artisan_name);
    setIsSearchOpen(false);
    logAnalyticsEvent('search', {
      search_term: s.artisan_name,
      type: s.type,
    });
    navigate(`/artisan/${s.id}`, { state: { background: location } });
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      saveRecentSearch(searchQuery.trim());
      setIsSearchOpen(false);
      logAnalyticsEvent('search', { search_term: searchQuery, type: 'manual' });
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const saveRecentSearch = (term) => {
    if (!term || term.trim() === '') return;
    const current = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const filtered = current.filter((s) => s.toLowerCase() !== term.toLowerCase());
    const updated = [term, ...filtered].slice(0, 5);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setRecentSearches(updated);
  };

  return (
    <>
      {/* SEARCH ICON (desktop) */}
      <button
        onClick={() => setIsSearchOpen(true)}
        className="hidden lg:block p-2 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
      >
        <Search size={20} />
      </button>

      {/* MOBILE SEARCH BUTTON */}
      <button
        onClick={() => setIsSearchOpen(true)}
        className="lg:hidden flex items-center gap-2 px-3 py-2 border rounded-lg text-sm text-gray-600"
      >
        <Search size={18} />
        Search
      </button>

      {/* SEARCH PANEL */}
      {isSearchOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[90] lg:hidden"
            onClick={() => setIsSearchOpen(false)}
          />
          <div
            ref={searchRef}
            className="
              fixed lg:absolute top-0 left-0 right-0 lg:inset-auto
              lg:top-full lg:left-0 lg:right-0
              bg-white lg:bg-white/90
              backdrop-blur-lg
              border-t lg:border
              z-[100]
              animate-slide-up
              shadow-xl lg:shadow-none
              rounded-b-2xl lg:rounded-none
            "
          >
            {/* MOBILE HEADER */}
            <div className="lg:hidden flex items-center justify-between px-4 py-4 border-b">
              <h3 className="font-semibold text-lg">Search</h3>
              <button onClick={() => setIsSearchOpen(false)}>
                <X size={22} />
              </button>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-6">
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    size={20}
                  />

                  <input
                    type="text"
                    placeholder="Search artisans, crafts, or locations"
                    className="
                    w-full pl-12 pr-4 py-3 sm:py-4
                    border-2 border-rose-200 rounded-xl
                    focus:outline-none focus:border-rose-500
                    text-base sm:text-lg
                  "
                    value={searchQuery}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />

                  {/* SUGGESTIONS */}
                  {searchQuery && (
                    <div
                      className="
                      absolute left-0 right-0 mt-2
                      bg-white border rounded-xl shadow-xl
                      max-h-72 overflow-y-auto
                      z-50
                    "
                    >
                      {suggestions.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No results found
                        </div>
                      ) : (
                        suggestions.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => fetchSearch(s)}
                            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100"
                          >
                            <img
                              src={s.profile_image || '/download.svg'}
                              alt={s.label || 'User avatar'}
                              className="w-9 h-9 rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = '/download.svg';
                              }}
                            />

                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                                {s.artisan_name}
                              </div>
                              <div className="text-xs text-gray-500 truncate mt-0.5">
                                {s.sub_label}
                              </div>
                            </div>
                            <span className="ml-auto text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">
                              {s.type}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* RECENT SEARCHES */}
                {!searchQuery && recentSearches.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-3">Recent searches</p>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((item) => (
                        <button
                          key={item}
                          onClick={() => {
                            saveRecentSearch(item);
                            navigate(`/search?q=${encodeURIComponent(item)}`);
                            setIsSearchOpen(false);
                          }}
                          className="
                          px-3 py-1 rounded-full text-sm font-semibold
                          bg-rose-100 text-rose-700
                          hover:bg-rose-200 transition
                        "
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default SearchBar;
